import { useCallback, useEffect, useState } from 'react';
import { FormTitle } from '../../components/FormTitle';
import { guardedExit, useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { SamplesPopup, TestsPopup, type PanelActions } from './SampleTransferPillPopups';
import { SampleTransferContainersTab, SampleTransferParametersTab } from './SampleTransferRegTabs';
import './sampleTransferDetail.css';

export type PillTab = 'samples' | 'tests' | 'containers' | 'params';

interface Props {
  jobOid: string;
  jobId: string;
  /** Which pill was clicked — decides the tab the page opens on. */
  initialTab: PillTab;
  /** The transfer job is SUBMITTED — the Samples panel's Delete is blocked
   *  (owner decision 2026-09-30, "block Delete only"). */
  submitted?: boolean;
  /** Return to the Sample Transfer detail view. */
  onBack: () => void;
}

const TABS: { id: PillTab; label: string; icon: string }[] = [
  { id: 'samples',    label: 'Samples',    icon: 'ti-flask' },
  { id: 'tests',      label: 'Tests',      icon: 'ti-test-pipe' },
  { id: 'containers', label: 'Containers', icon: 'ti-box' },
  { id: 'params',     label: 'Parameters', icon: 'ti-list-details' },
];

/**
 * Sample Transfer → Samples / Tests / Containers as a FULL PAGE with a tab strip,
 * replacing the three separate modals (user request 2026-09-23, Sample Registration
 * parity). Two reasons the modal had to go:
 *  • the Sample Login grid is ~48 columns wide — a 1180px dialog can only show it
 *    with horizontal scrolling;
 *  • the dialog covered the transfer form, so the operator lost the job context.
 *
 * The grids themselves are UNCHANGED — the same components render here with
 * `embedded`, so editability, columns and toolbars cannot drift between the two
 * shells.
 *
 * Operators EDIT sample fields here (confirmed 2026-09-23), so unlike the modal
 * this page can be navigated away from mid-edit. Grid cell editors are invisible
 * to `installUnsavedTracking()`, so the Sample Login panel reports its dirty state
 * up and we arm `useUnsavedGuard` explicitly — standing rule 15. Save runs through
 * the shared `useSaveFlow` inside the panel, which clears the flag on success.
 */
export default function SampleTransferSamplesPage({ jobOid, jobId, initialTab, submitted, onBack }: Props) {
  const [tab, setTab] = useState<PillTab>(initialTab);
  const [dirty, setDirty] = useState(false);
  // Save / Save and Close / Cancel are published by the active panel and rendered
  // on THIS row — the top-right of the tab bar, where Registration puts them
  // (user request 2026-09-23). Null while the open tab has nothing to save.
  const [acts, setActs] = useState<PanelActions | null>(null);

  useUnsavedGuard(dirty);

  // Leaving the page (Back / breadcrumb / tab switch) must not silently drop edits.
  const leave = useCallback((go: () => void) => { void guardedExit(go); }, []);

  // Switching tab unmounts the editable grid, so it is an exit too.
  const switchTab = useCallback((next: PillTab) => {
    if (next === tab) return;
    if (dirty) { leave(() => { setDirty(false); setTab(next); }); return; }
    setTab(next);
  }, [tab, dirty, leave]);

  // A tab that cannot be dirty must not leave a stale flag armed behind it.
  useEffect(() => { if (tab !== 'samples') setDirty(false); }, [tab]);

  // The Save trio for the tab row. The Samples tab has a REAL batch save that its
  // panel publishes via `acts` (disabled until it is ready). The Tests / Containers /
  // Parameters tabs persist each edit immediately (receipt-safe endpoints), so their
  // Save just acknowledges and Save and Close returns to the detail view — always
  // enabled, matching Registration (user request 2026-09-29).
  // Only the Samples tab commits edits, so only it shows the Save trio. Tests /
  // Containers / Parameters are READ-ONLY viewers with Export the only action (legacy
  // parity — owner report 2026-09-30), so they publish no Save trio.
  const saveActs: PanelActions | null = tab === 'samples' ? acts : null;

  return (
    // `bl-page` supplies the grey page background + scroll frame that every other
    // form uses; without it the content sat flush on white with no card contrast
    // (user report 2026-09-23 — "Header not Good. Alignment").
    <div className="bl-page stx-detail">
      <div className="bl-top">
        {/* `dp-page-hdr` is the standard header block (backlog.css: padding
            16px 0 10px). Without it the breadcrumb sat hard against the app
            header with no top padding — "looks like upward" (user report
            2026-09-23). The detail page and the other standard forms wrap the
            breadcrumb + title row in it; this page did not. */}
        <div className="dp-page-hdr">
        {/* The ONE app-standard breadcrumb (`dp-breadcrumb` + `dp-bc-link` +
            `dp-bc-active`, backlog.css) that 70+ forms use — this page was on
            `dp-bc`, which has NO css at all, so it rendered unstyled with orange
            `stx-link` text instead of the standard 11px muted row with blue
            links and a 6px bottom margin (user report 2026-09-23). */}
        <div className="dp-breadcrumb">
          <span className="dp-bc-link" onClick={() => leave(onBack)}>Sample Transfer</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-link" onClick={() => leave(onBack)}>{jobId}</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">{TABS.find(t => t.id === tab)?.label}</span>
        </div>
        <div className="dp-title-row">
          <FormTitle pageId="samptransfer" title={`Sample Transfer – ${jobId}`} />
        </div>
        </div>
      </div>

      {/* Everything below lives in ONE bordered card with inner padding, exactly
          like Sample Registration's .sr-sec / .sr-sec-b. Previously the tab row,
          toolbar, finder and grid all ran edge-to-edge with no gutter. */}
      <div className="stx-pill-sec">
        <div className="stx-pill-sec-b">
          {/* Back button + text tab strip on ONE row, Registration's anatomy. */}
          <div className="stx-pill-head">
            <button type="button" className="ca-btn" onClick={() => leave(onBack)}
              title="Back to the Sample Transfer detail view">
              <span style={{ color: 'var(--ai-blue)' }}>‹</span> Sample Transfer
            </button>
            <div className="stx-pill-tabs" role="tablist">
              {TABS.map(t => (
                <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
                  className={`stx-pill-tab${tab === t.id ? ' is-active' : ''}`}
                  onClick={() => switchTab(t.id)}>
                  {t.label}
                </button>
              ))}
            </div>
            {/* Save / Save and Close / Cancel render on EVERY tab and stay ENABLED,
                matching Registration (user request 2026-09-29). The Samples tab has a
                real batch save published by its panel (`acts`); the Tests / Containers /
                Parameters tabs persist each edit immediately through Sample Transfer's
                OWN receipt-safe endpoints, so on those tabs Save just acknowledges (the
                changes are already saved) and Save and Close returns to the detail view —
                so the trio always works instead of being greyed out. */}
            {tab === 'samples' && (
              <div className="stx-pill-save">
                <button type="button" className="ca-btn ca-btn-solid"
                  disabled={!saveActs} title="Save"
                  onClick={() => saveActs?.save()}>Save</button>
                <button type="button" className="ca-btn"
                  disabled={!saveActs} title="Save and close"
                  onClick={() => saveActs?.saveAndClose()}>Save and Close</button>
                <button type="button" className="ca-btn" onClick={() => leave(onBack)}>Cancel</button>
              </div>
            )}
          </div>

          {/* `embedded` renders the SAME panel body without the dialog chrome. */}
          {tab === 'samples' && (
            <SamplesPopup jobOid={jobOid} jobId={jobId} embedded submitted={submitted} onClose={onBack}
              onDirtyChange={setDirty} onActionsChange={setActs} />
          )}
          {tab === 'tests' && <TestsPopup jobOid={jobOid} jobId={jobId} embedded onClose={onBack} />}
          {/* Registration's OWN Containers tab (All in One / By Sample + Copy /
              Reset Bottles), not a second implementation - owner request 2026-09-28. */}
          {tab === 'containers' && <SampleTransferContainersTab jobOid={jobOid} jobId={jobId} />}
          {tab === 'params' && <SampleTransferParametersTab jobOid={jobOid} />}
        </div>
      </div>
    </div>
  );
}
